//@ts-check

import moment from 'moment'
import 'moment-timezone'

export const DEFAULT_TIMEZONE = 'Europe/Amsterdam'

export class HomeDateSupport {
    /**
     * Create a new instance of the `HomeDateSupport` class, which provides
     * functionality to support dates in the home's timezone.
     *
     * @param {string} timezone The home's timezone, e.g. `Europe/Amsterdam`.
     */
    constructor(timezone) {
        this.timezone = timezone ?? DEFAULT_TIMEZONE

        if (!moment.tz.zone(timezone)) {
            console.error(`Timezone "${timezone}" is not included in graph renderer bundle. Falling back to ${DEFAULT_TIMEZONE}.`)
            this.timezone = DEFAULT_TIMEZONE
        }
    }

    /**
     * The current date/time in the home's timezone.
     *
     * @returns {moment.Moment} The current date/time in the home's timezone.
    */
    now() {
        return moment.tz(this.timezone)
    }

    /**
     * Get a date object from a timestamp in milliseconds.
     *
     * Throws an error if the timestamp is not a number or if the timestamp is
     * invalid.
     *
     * @param {number} timestampMillis The timestamp in milliseconds (in the
     * home's timezone).
     *
     * @returns {moment.Moment} The resulting date object.
     */
    getDateFromMillis(timestampMillis) {
        if (typeof (timestampMillis) !== 'number') {
            throw new Error(`Expected a numeric timestamp, got ${timestampMillis}`)
        }

        const parsed = moment.tz(timestampMillis, this.timezone)
        if (!parsed.isValid()) {
            throw new Error(`Failed to convert timestamp to date: ${timestampMillis}`)
        }

        return parsed
    }

    /**
     * Parse a formatted timestamp.
     *
     * @param {string} timestamp The formatted timestamp (in the home's
     * timezone).
     *
     * @param {moment.MomentFormatSpecification} format The moment.js format
     * string (for example `YYYY/MM/DD`).
     */
    parseDateString(timestamp, format) {
        if (typeof (timestamp) !== 'string') {
            throw new Error(`Expected a formatted timestamp, got ${timestamp}`)
        }

        const parsed = moment.tz(timestamp, format, this.timezone)
        if (!parsed.isValid()) {
            throw new Error(`Failed to parse timestamp ${timestamp} using ${format}.`)
        }

        return parsed
    }

    /**
     * Convert a timestamp to a TSDB-formatted date string.
     *
     * @param {number} timestampMillis
     * @param {'day'|'week'|'month'} view The target "view".
     *
     * @return {string|undefined} A TSDB-formatted string:
     *  * `day` -> 2023/01/01
     *  * `week` -> 2023/week/01
     *  * `month` -> 2023/01
     *
     *  Or `undefined` if the view is not supported.
    */
    formatTsdbPeriod(timestampMillis, view) {
        const date = this.getDateFromMillis(timestampMillis)
        switch (view) {
            case 'day':
                return date.format('YYYY/MM/DD')

            case 'week':
                return `${date.format('YYYY')}/week/${date.format('W').padStart(2, '0')}`

            case 'month':
                return date.format('YYYY/MM')
        }
    }

    /**
     * Get a tick label for the provided timestamp, suitable for the provided
     * graph view.
     *
     * @param {number} timestampMillis The timestamp.
     * @param {string} graphView The target "view", e.g. `year`, `month`,
     * `week`, `day`, `hour`.
     *
     * @return {string|undefined} A tick label:
     * * `year` -> first letter of the month name, e.g. `J` for January
     * * `month` -> (ISO) week number, e.g. `1`
     * * `week` -> day of the week, e.g. `mo` for Monday
     * * `day` -> time of day, e.g. `12:00`
     * * `hour` -> time of day, e.g. `12:00:00`
     *
     *  Or `undefined` if the view is not supported.
     */
    formatGraphTick(timestampMillis, graphView) {
        const date = this.getDateFromMillis(timestampMillis)
        switch (graphView) {
            case 'year':
                return date.format('MMMM').charAt(0)

            case 'month':
                return date.format('W')

            case 'week':
                return date.format('dd').toLowerCase()

            case 'day':
                return date.format('HH:mm')

            case 'hour':
                return date.format('HH:mm:ss')
        }
    }

    /**
     * Indicates whether the provided date is in the current year.
     *
     * We'll generally want to hide the year in the graph tooltips, unless we're
     * looking at a date in a different year.
     *
     * @param {moment.Moment} date The date to check.
     * @returns {boolean} `true` if the date's year is the current year (in the
     * home's timezone), `false` otherwise.
     */
    isInCurrentYear(date) {
        return date.isSame(this.now(), 'year')
    }

    /**
     * Indicates whether the date is in the future (as opposed to being in the
     * past or present).
     *
     * @param {moment.Moment} date The date to check.
     * @returns {boolean} `true` if the date is in the future, `false` if it's
     * in the past or present.
     */
    isInFuture(date) {
        return date.isAfter(this.now())
    }
}
